<template>
    <article>
        <router-link
            :to="{
                name: 'NewsPage',
                params: { id: post.id },
            }">
            <PostImg :src="post.imgSrc">
                <div class="highlighted_post-title-wrapper">
                    <h4 class="tw-ml-auto tw-mr-5 tw-mt-4 tw-rounded-full tw-bg-dark tw-px-2 tw-py-1 tw-text-xs tw-font-normal tw-text-white sm:tw-text-sm">{{ post.dayDate }}</h4>
                    <h2
                        id="pretitle"
                        class="highlighted_post-title" v-html="post.title">
                    </h2>
                    <div
                        v-if="titleParts.length > 0"
                        class="tw-z-[1] tw-flex tw-flex-col tw-gap-1">
                        <h2
                            v-for="(line, index) in titleParts"
                            :key="index"
                            class="highlighted_post-title">
                            {{ line }}
                        </h2>
                    </div>
                </div>
            </PostImg>
        </router-link>
    </article>
</template>

<script setup lang="ts">
    import _ from 'lodash';
    import type { WPPost } from '@/contentTypes';
    import type { Category } from '@/configs/categories';
    import { usePost } from '@/composables/usePost';
    import { useWindowSize } from '@vueuse/core';

    const props = defineProps<{
        post: WPPost;
        category?: Category;
    }>();

    const $route = useRoute();

    const post = computed(() => usePost(props.post, props.category));

    const colorVariable = computed(() => {
        return post.value.colorVariable;
    });

    const titleParts = ref<string[]>([]);

    function splitTextIntoLines(query: string) {
        const el = document.querySelector(query) as HTMLElement | null;
        if (!el) return;

        const wrapper = el.parentElement;
        if (!wrapper) return;
        const wrapperWidth = Math.min(wrapper.clientWidth * 0.75, 600); // 70% of the wrapper width
        const words = el.textContent?.split(' ') || [];

        let currentLine = '';
        titleParts.value = [];
        words.forEach((word) => {
            const testLine = currentLine ? `${currentLine} ${word}` : word;
            const testEl = document.createElement('h2');
            testEl.className = 'highlighted_post-title';
            testEl.style.visibility = 'hidden';
            testEl.style.position = 'absolute';
            testEl.textContent = testLine;
            document.body.appendChild(testEl);

            if (testEl.clientWidth > wrapperWidth) {
                titleParts.value.push(currentLine);
                currentLine = word; // Start new line with the current word
            } else {
                currentLine = testLine; // Continue adding to the current line
            }
            document.body.removeChild(testEl);
        });

        if (currentLine) {
            titleParts.value.push(currentLine); // Add the last line
        }

        // set display none to the original element
        el.style.display = 'none';
    }

    onMounted(() => {
        splitTextIntoLines('#pretitle.highlighted_post-title');
    });

    onUpdated(() => {
        splitTextIntoLines('#pretitle.highlighted_post-title');
    });

    const { width } = useWindowSize();
    watch(width, () => {
        splitTextIntoLines('#pretitle.highlighted_post-title');
    });

    watch(
        () => $route.fullPath,
        () => {
            nextTick(() => {
                if ($route.name === 'News' && $route.params?.category === props.category?.value) {
                    splitTextIntoLines('#pretitle.highlighted_post-title');
                }
            });
        },
    );
</script>

<style>
    .highlighted_post-title-wrapper {
        @apply tw-absolute tw-inset-0 tw-flex tw-flex-col tw-justify-between tw-rounded-lg tw-bg-gradient-to-b tw-from-transparent tw-to-dark tw-pb-4;
    }

    .highlighted_post-title-wrapper::before {
        @apply tw-absolute tw-inset-2 tw-flex tw-flex-col tw-justify-end tw-rounded-lg tw-border tw-border-solid tw-pb-4 sm:tw-border-2;
        border-color: rgb(v-bind(colorVariable));
        content: '';
    }

    .highlighted_post-title {
        @apply tw-ms-2 tw-w-fit tw-px-2 tw-font-title tw-text-base tw-font-bold tw-leading-relaxed tw-text-white sm:tw-text-xl sm:tw-leading-loose;
        background-color: rgb(v-bind(colorVariable));
    }

    .highlighted_post-title:last-of-type {
        @apply tw-mb-4 sm:tw-mb-10;
    }
</style>
